import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ChevronLeft, ChevronRight, Save, Sparkles, Check, Building2, Users, Plus, Mail, Download } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

function chf(n) {
  return "CHF " + Number(n || 0).toLocaleString("de-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const STEPS = [
  { key: "service", label: "Leistung", icon: Building2 },
  { key: "scope", label: "Umfang", icon: Users },
  { key: "addons", label: "Zusatzleistungen", icon: Plus },
  { key: "result", label: "Ihr Angebot", icon: Sparkles },
];

export default function CalculatorPage() {
  const { user } = useAuth();
  const [catalog, setCatalog] = useState(null);
  const [catalogError, setCatalogError] = useState(null);
  const [step, setStep] = useState(0);
  const [data, setData] = useState({
    service: "",
    complexity: "standard",
    employees: 0,
    annual_revenue_chf: 0,
    addons: [],
  });
  const [result, setResult] = useState(null);
  const [busyResult, setBusyResult] = useState(false);
  const [saving, setSaving] = useState(false);
  const [agbAccepted, setAgbAccepted] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async (attempt = 0) => {
      try {
        const { data } = await api.get("/calculator/catalog", { withCredentials: false, timeout: 10000 });
        if (!cancelled) { setCatalog(data); setCatalogError(null); }
      } catch (e) {
        if (cancelled) return;
        if (attempt < 2) {
          setTimeout(() => load(attempt + 1), 1500);
        } else {
          setCatalogError("Der Kostenrechner konnte nicht geladen werden. Bitte Seite neu laden oder versuchen Sie es später erneut.");
        }
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const selectedService = useMemo(
    () => catalog?.services?.find((s) => s.key === data.service),
    [catalog, data.service]
  );

  // Compute when reaching the result step (or whenever inputs change on the result step)
  useEffect(() => {
    if (step !== STEPS.length - 1 || !data.service) return;
    const fetchEstimate = async () => {
      setBusyResult(true);
      try {
        const { data: res } = await api.post("/calculator/estimate", { ...data, save: false });
        setResult(res);
      } catch {
        toast.error("Berechnung fehlgeschlagen.");
      } finally {
        setBusyResult(false);
      }
    };
    fetchEstimate();
  }, [step, data]);

  const next = () => {
    if (step === 0 && !data.service) {
      toast.error("Bitte wählen Sie eine Leistung.");
      return;
    }
    setStep((s) => Math.min(STEPS.length - 1, s + 1));
  };
  const prev = () => setStep((s) => Math.max(0, s - 1));

  const toggleAddon = (k) =>
    setData((d) => ({
      ...d,
      addons: d.addons.includes(k) ? d.addons.filter((x) => x !== k) : [...d.addons, k],
    }));

  const save = async () => {
    setSaving(true);
    try {
      const { data: res } = await api.post("/calculator/estimate", { ...data, save: true });
      setResult(res);
      toast.success("Angebot in Ihrem Portal gespeichert.");
    } catch {
      toast.error("Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  };

  if (catalogError) return (
    <div className="pt-32 pb-24 text-center max-w-md mx-auto">
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-red-800">
        <p className="font-medium mb-3">{catalogError}</p>
        <button onClick={() => window.location.reload()} className="btn-primary justify-center mx-auto">
          Seite neu laden
        </button>
      </div>
    </div>
  );
  if (!catalog) return <div className="pt-32 text-center text-slate-500">Lade Kostenrechner…</div>;

  return (
    <div className="pt-32 pb-24 bg-slate-50 min-h-screen" data-testid="calculator-page">
      <div className="mbw-container max-w-5xl">
        <div className="text-center mb-12">
          <div className="mbw-overline mb-3">Kostenrechner</div>
          <h1 className="font-heading text-4xl md:text-6xl font-bold tracking-tighter text-[#1A2433] leading-[1.05] mb-3">
            Was kostet das? <span className="text-[#991B1B]">Sofort & transparent.</span>
          </h1>
          <p className="text-slate-600 max-w-2xl mx-auto">
            In vier Schritten zu Ihrer unverbindlichen Kostenschätzung.
          </p>
        </div>

        {/* Stepper */}
        <div className="flex items-center justify-between mb-10" data-testid="calc-stepper">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            const isDone = i < step;
            const isCurrent = i === step;
            return (
              <React.Fragment key={s.key}>
                <button
                  onClick={() => i < step && setStep(i)}
                  className={`flex flex-col items-center gap-2 group ${i < step ? "cursor-pointer" : "cursor-default"}`}
                  data-testid={`calc-step-${i}`}
                >
                  <div
                    className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${
                      isDone
                        ? "bg-[#991B1B] text-white"
                        : isCurrent
                        ? "bg-[#1A2433] text-white ring-4 ring-[#991B1B]/20"
                        : "bg-white border-2 border-slate-200 text-slate-400"
                    }`}
                  >
                    {isDone ? <Check className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                  </div>
                  <span className={`text-xs font-semibold uppercase tracking-wider hidden sm:block ${isCurrent ? "text-[#1A2433]" : "text-slate-500"}`}>
                    {s.label}
                  </span>
                </button>
                {i < STEPS.length - 1 && (
                  <div className={`flex-1 h-0.5 mx-2 ${i < step ? "bg-[#991B1B]" : "bg-slate-200"}`} />
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* Step content */}
        <div className="bg-white rounded-2xl border border-slate-200 p-8 md:p-12 min-h-[400px]">
          {step === 0 && (
            <div data-testid="calc-step-service">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-[#1A2433] mb-2">Welche Leistung benötigen Sie?</h2>
              <p className="text-slate-600 mb-8">Wählen Sie die für Sie passende Hauptleistung aus.</p>
              <div className="grid sm:grid-cols-2 gap-3">
                {catalog.services.map((s) => (
                  <button
                    key={s.key}
                    onClick={() => setData({ ...data, service: s.key })}
                    className={`text-left p-5 rounded-xl border-2 transition-all ${
                      data.service === s.key
                        ? "border-[#991B1B] bg-[#991B1B]/5 shadow-sm"
                        : "border-slate-200 hover:border-slate-300 hover:bg-slate-50"
                    }`}
                    data-testid={`calc-service-${s.key}`}
                  >
                    <div className="font-heading font-semibold text-[#1A2433] mb-1">{s.label}</div>
                    <div className="text-sm text-slate-500">ab {chf(s.base)}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 1 && (
            <div data-testid="calc-step-scope">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-[#1A2433] mb-2">Wie ist der Umfang?</h2>
              <p className="text-slate-600 mb-8">Diese Angaben helfen uns, ein präzises Angebot zu erstellen.</p>

              <div className="space-y-8">
                <div>
                  <label className="block text-sm font-semibold text-[#1A2433] mb-3">Komplexität Ihres Falles</label>
                  <div className="grid md:grid-cols-3 gap-3">
                    {catalog.complexities.map((c) => {
                      const active = data.complexity === c.key;
                      return (
                        <button
                          key={c.key}
                          onClick={() => setData({ ...data, complexity: c.key })}
                          className={`text-left p-4 rounded-xl border-2 transition-all ${
                            active
                              ? "border-[#991B1B] bg-[#991B1B]/5"
                              : "border-slate-200 hover:border-slate-300 bg-white"
                          }`}
                          data-testid={`calc-complexity-${c.key}`}
                        >
                          <div className="flex items-baseline justify-between mb-1">
                            <span className="font-heading font-semibold text-[#1A2433] capitalize">{c.label || c.key}</span>
                            <span className="text-[10px] uppercase tracking-wider text-slate-500">Faktor {c.multiplier}×</span>
                          </div>
                          {c.description && (
                            <p className="text-xs text-slate-600 leading-snug mt-2">{c.description}</p>
                          )}
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-xs text-slate-500 mt-3">
                    Unsicher? Auf der Seite <a href="/so-funktionierts" className="text-[#991B1B] underline hover:no-underline">„So funktioniert's"</a> finden Sie eine ausführliche Einordnung pro Leistung.
                  </p>
                </div>

                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-[#1A2433] mb-3">Anzahl Mitarbeitende</label>
                    <input
                      type="number"
                      min="0"
                      value={data.employees}
                      onChange={(e) => setData({ ...data, employees: Number(e.target.value) })}
                      className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-[#991B1B]"
                      data-testid="calc-employees"
                    />
                    <p className="text-xs text-slate-500 mt-1">Für Lohnverwaltung relevant.</p>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-[#1A2433] mb-3">Erwarteter Jahresumsatz (CHF)</label>
                    <input
                      type="number"
                      min="0"
                      step="10000"
                      value={data.annual_revenue_chf}
                      onChange={(e) => setData({ ...data, annual_revenue_chf: Number(e.target.value) })}
                      className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-[#991B1B]"
                      data-testid="calc-revenue"
                    />
                    <p className="text-xs text-slate-500 mt-1">Beeinflusst den Pauschalpreis bei hohen Umsätzen.</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div data-testid="calc-step-addons">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-[#1A2433] mb-2">Zusatzleistungen</h2>
              <p className="text-slate-600 mb-8">Optional – wählen Sie alles, was für Ihren Fall passt.</p>
              <div className="grid sm:grid-cols-2 gap-3">
                {catalog.addons.map((a) => {
                  const active = data.addons.includes(a.key);
                  return (
                    <button
                      key={a.key}
                      onClick={() => toggleAddon(a.key)}
                      className={`flex items-center justify-between text-left p-4 rounded-xl border-2 transition-all ${
                        active ? "border-[#991B1B] bg-[#991B1B]/5" : "border-slate-200 hover:border-slate-300"
                      }`}
                      data-testid={`calc-addon-${a.key}`}
                    >
                      <div>
                        <div className="font-medium text-[#1A2433]">{a.label}</div>
                        <div className="text-sm text-slate-500">+ {chf(a.price)}</div>
                      </div>
                      <div
                        className={`w-6 h-6 rounded-md border-2 flex items-center justify-center flex-shrink-0 ml-3 ${
                          active ? "bg-[#991B1B] border-[#991B1B]" : "border-slate-300"
                        }`}
                      >
                        {active && <Check className="w-4 h-4 text-white" />}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {step === 3 && (
            <div data-testid="calc-step-result">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-5 h-5 text-[#991B1B]" />
                <span className="mbw-overline">Ihre individuelle Schätzung</span>
              </div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-[#1A2433] mb-8">
                {result?.service_label || (selectedService ? selectedService.label : "—")}
              </h2>

              {busyResult || !result ? (
                <div className="text-slate-500">Berechne…</div>
              ) : (
                <div className="grid lg:grid-cols-2 gap-10">
                  <div className="space-y-3">
                    {result.breakdown.map((b, i) => (
                      <div key={i} className="flex justify-between text-sm py-2 border-b border-slate-100">
                        <span className="text-slate-600">{b.label}</span>
                        <span className="tabular-nums font-medium text-[#1A2433]">{chf(b.amount)}</span>
                      </div>
                    ))}
                    <div className="flex justify-between text-sm pt-3">
                      <span className="text-slate-600">Zwischensumme</span>
                      <span className="tabular-nums font-medium text-[#1A2433]">{chf(result.subtotal_chf)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">MWST 8.1%</span>
                      <span className="tabular-nums font-medium text-[#1A2433]">{chf(result.vat_chf)}</span>
                    </div>
                  </div>

                  <div className="bg-[#1A2433] text-white rounded-2xl p-8">
                    <div className="text-xs uppercase tracking-[0.2em] text-slate-400 mb-3">Gesamtbetrag inkl. MWST</div>
                    <div className="font-heading text-5xl font-bold text-[#991B1B] mb-2 tabular-nums" data-testid="calc-total">
                      {chf(result.total_chf)}
                    </div>
                    <div className="text-xs text-slate-400 mb-8">Gültig {result.valid_days} Tage · unverbindlich</div>

                    <div className="space-y-3">
                      <label className="flex items-start gap-2 text-xs text-slate-300 cursor-pointer select-none text-left" data-testid="calc-agb-wrapper">
                        <input
                          type="checkbox"
                          checked={agbAccepted}
                          onChange={(e) => setAgbAccepted(e.target.checked)}
                          className="mt-0.5 w-4 h-4 accent-[#DC2626]"
                          data-testid="calc-agb-checkbox"
                        />
                        <span>
                          Ich habe die <Link to="/impressum#agb" className="text-[#DC2626] underline hover:no-underline">AGB</Link> und die{" "}
                          <Link to="/datenschutz" className="text-[#DC2626] underline hover:no-underline">Datenschutzerklärung</Link> gelesen und akzeptiere diese.
                        </span>
                      </label>
                      {user && user.id ? (
                        <button
                          onClick={save}
                          disabled={saving || !agbAccepted}
                          className="btn-primary w-full justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                          data-testid="calc-save-btn"
                        >
                          <Save className="w-4 h-4" /> {saving ? "Speichere…" : "Im Portal speichern"}
                        </button>
                      ) : (
                        <a
                          href={agbAccepted ? "/register" : undefined}
                          onClick={(e) => { if (!agbAccepted) e.preventDefault(); }}
                          aria-disabled={!agbAccepted}
                          className={`btn-primary w-full justify-center ${!agbAccepted ? "opacity-50 cursor-not-allowed" : ""}`}
                          data-testid="calc-register-cta"
                        >
                          <Save className="w-4 h-4" /> Konto anlegen &amp; speichern
                        </a>
                      )}
                      <a
                        href={`/kontakt?leistung=${encodeURIComponent(result.service_label)}&betrag=${result.total_chf}`}
                        className="bg-white text-[#1A2433] hover:bg-slate-100 font-semibold rounded-md px-6 py-3 flex items-center justify-center gap-2 transition-colors"
                        data-testid="calc-contact-cta"
                      >
                        <Mail className="w-4 h-4" /> Verbindliches Angebot anfordern
                      </a>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer nav — prominent, gut sichtbar */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 mt-8 pt-6 border-t-2 border-slate-200 bg-slate-50 -mx-6 -mb-6 px-6 pb-6 sm:rounded-b-2xl">
          <button
            onClick={prev}
            disabled={step === 0}
            className="inline-flex items-center justify-center gap-2 px-5 py-3 border-2 border-slate-300 text-slate-700 font-semibold rounded-lg hover:bg-white hover:border-slate-400 disabled:opacity-40 disabled:cursor-not-allowed transition"
            data-testid="calc-prev"
          >
            <ChevronLeft className="w-5 h-5" /> Zurück
          </button>
          {step < STEPS.length - 1 ? (
            <button
              onClick={next}
              className="inline-flex items-center justify-center gap-2 px-7 py-3 bg-[#991B1B] hover:bg-[#7c1414] text-white font-bold rounded-lg shadow-lg shadow-red-900/20 hover:shadow-xl transition text-base"
              data-testid="calc-next"
            >
              Weiter <ChevronRight className="w-5 h-5" />
            </button>
          ) : (
            <button
              onClick={() => { setStep(0); setData({ service: "", complexity: "standard", employees: 0, annual_revenue_chf: 0, addons: [] }); setResult(null); }}
              className="inline-flex items-center justify-center gap-2 px-5 py-3 border-2 border-[#1A2433] text-[#1A2433] font-semibold rounded-lg hover:bg-[#1A2433] hover:text-white transition"
              data-testid="calc-restart"
            >
              Neu berechnen
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
